import { getDeck, isDeckId, type DeckId } from './decks.js';
import {
  applyDisconnect,
  applyJoin,
  applyLeave,
  applySetRole,
  MAX_PARTICIPANTS,
  MAX_TITLE_LENGTH,
  type Participant,
  type Role,
} from './seats.js';
import { summarizeVotes, type VoteSummary } from './stats.js';

export { MAX_PARTICIPANTS, MAX_NAME_LENGTH, MAX_TITLE_LENGTH, type Role, type Participant } from './seats.js';

export const MAX_QUEUE_LENGTH = 50;

export type RoundPhase = 'voting' | 'revealed';

/** A story waiting its turn. Public information — a queue hides nothing. */
export interface QueuedStory {
  readonly id: string;
  readonly title: string;
}

export interface Round {
  readonly title: string;
  readonly startedAt: number;
  readonly revealedAt: number | null;
  /** participantId → card value. Redacted before it reaches other clients. */
  readonly votes: Readonly<Record<string, string>>;
}

export interface CompletedRound {
  readonly id: string;
  readonly title: string;
  readonly votes: Readonly<Record<string, string>>;
  /** Names captured at archive time so history survives people leaving. */
  readonly participantNames: Readonly<Record<string, string>>;
  readonly summary: VoteSummary;
  readonly revealedAt: number | null;
  readonly endedAt: number;
}

export interface Room {
  readonly kind: 'planning';
  readonly id: string;
  readonly deckId: DeckId;
  readonly hostId: string | null;
  readonly participants: Readonly<Record<string, Participant>>;
  readonly round: Round;
  readonly queue: readonly QueuedStory[];
  /**
   * Monotonic source of queue ids. A completed round can derive its id from
   * `history.length` because history only grows; the queue shrinks, so it needs
   * a counter that never rewinds to stay collision-free.
   */
  readonly queueSeq: number;
  /** Turn the table over by itself once every player has played. */
  readonly autoReveal: boolean;
  readonly history: readonly CompletedRound[];
  readonly createdAt: number;
}

export type Command =
  | { readonly type: 'join'; readonly name: string; readonly role: Role }
  | { readonly type: 'leave' }
  | { readonly type: 'disconnect' }
  | { readonly type: 'setRole'; readonly role: Role }
  | { readonly type: 'castVote'; readonly value: string }
  | { readonly type: 'retractVote' }
  | { readonly type: 'reveal' }
  | { readonly type: 'reset' }
  | { readonly type: 'setTitle'; readonly title: string }
  | { readonly type: 'nextRound'; readonly title: string }
  | { readonly type: 'setDeck'; readonly deckId: DeckId }
  | { readonly type: 'queueStories'; readonly titles: readonly string[] }
  | { readonly type: 'removeQueuedStory'; readonly storyId: string }
  | { readonly type: 'setAutoReveal'; readonly enabled: boolean }
  | { readonly type: 'transferHost'; readonly participantId: string }
  | { readonly type: 'removeParticipant'; readonly participantId: string };

export interface CommandMeta {
  readonly actorId: string;
  readonly now: number;
}

export type ErrorCode =
  | 'invalid_name'
  | 'invalid_title'
  | 'room_full'
  | 'not_in_room'
  | 'host_only'
  | 'observers_cannot_vote'
  | 'card_not_in_deck'
  | 'round_revealed'
  | 'no_votes'
  | 'unknown_deck'
  | 'queue_full'
  | 'unknown_participant'
  | 'participant_away'
  | 'participant_connected'
  | 'cannot_remove_self';

export interface CommandError {
  readonly code: ErrorCode;
  readonly message: string;
}

export type CommandResult = { readonly ok: true; readonly room: Room } | { readonly ok: false; readonly error: CommandError };

const ERROR_MESSAGES: Record<ErrorCode, string> = {
  invalid_name: 'Enter a display name.',
  invalid_title: 'That story title is too long.',
  room_full: `This session already has ${MAX_PARTICIPANTS} people in it.`,
  not_in_room: 'You are not in this session.',
  host_only: 'Only the host can do that.',
  observers_cannot_vote: 'Observers watch, they do not vote.',
  card_not_in_deck: 'That card is not in this deck.',
  round_revealed: 'This round is already revealed.',
  no_votes: 'Nobody has voted yet.',
  unknown_deck: 'That deck does not exist.',
  queue_full: `The queue holds ${MAX_QUEUE_LENGTH} stories at most.`,
  unknown_participant: 'That person is not in this session.',
  participant_away: 'They are away — hand the session to someone who is here.',
  participant_connected: 'You can only clear a seat that has gone away.',
  cannot_remove_self: 'Leave the session instead.',
};

function fail(code: ErrorCode): CommandResult {
  return { ok: false, error: { code, message: ERROR_MESSAGES[code] } };
}

function succeed(room: Room): CommandResult {
  return { ok: true, room };
}

export function roundPhase(round: Round): RoundPhase {
  return round.revealedAt === null ? 'voting' : 'revealed';
}

export function createRoom(options: { id: string; deckId: DeckId; now: number }): Room {
  return {
    kind: 'planning',
    id: options.id,
    deckId: options.deckId,
    hostId: null,
    participants: {},
    round: emptyRound('', options.now),
    queue: [],
    queueSeq: 0,
    autoReveal: false,
    history: [],
    createdAt: options.now,
  };
}

/**
 * Whether the current round is still waiting for its first story, in which case
 * a freshly pasted list should start straight away rather than making the host
 * press "Next story" to begin.
 */
function awaitingFirstStory(room: Room): boolean {
  return room.round.title === '' && Object.keys(room.round.votes).length === 0 && roundPhase(room.round) === 'voting';
}

/**
 * Every player has played. Disconnected players still count — someone who shut
 * their laptop has not voted, and saying otherwise would be a convenient lie. The
 * host clears a seat like that deliberately.
 */
export function allPlayersVoted(room: Room): boolean {
  const players = Object.values(room.participants).filter((participant) => participant.role === 'player');
  return players.length > 0 && players.every((participant) => participant.id in room.round.votes);
}

/**
 * Reveals when the room is set to do so and the table is complete. Applied only
 * after a deliberate act — a vote, or a role change — never after a disconnect or
 * a departure, because a network blip must not turn the table over.
 */
function maybeAutoReveal(room: Room, now: number): Room {
  if (!room.autoReveal || roundPhase(room.round) === 'revealed') return room;
  if (!allPlayersVoted(room)) return room;
  return { ...room, round: { ...room.round, revealedAt: now } };
}

function emptyRound(title: string, now: number): Round {
  return { title, startedAt: now, revealedAt: null, votes: {} };
}

function withoutVote(votes: Readonly<Record<string, string>>, participantId: string): Readonly<Record<string, string>> {
  if (!(participantId in votes)) return votes;
  const next = { ...votes };
  delete next[participantId];
  return next;
}

function archiveCurrentRound(room: Room, now: number): Room {
  if (Object.keys(room.round.votes).length === 0) return room;

  const participantNames: Record<string, string> = {};
  for (const participantId of Object.keys(room.round.votes)) {
    const name = room.participants[participantId]?.name;
    participantNames[participantId] = name ?? 'Left the session';
  }

  const completed: CompletedRound = {
    id: `${room.id}-${room.history.length + 1}`,
    title: room.round.title,
    votes: room.round.votes,
    participantNames,
    summary: summarizeVotes(room.round.votes),
    revealedAt: room.round.revealedAt,
    endedAt: now,
  };

  return { ...room, history: [...room.history, completed] };
}

export function applyCommand(room: Room, command: Command, meta: CommandMeta): CommandResult {
  const { actorId, now } = meta;
  const actor = room.participants[actorId];

  if (command.type === 'join') {
    const result = applyJoin(room, actorId, command.name, command.role, now);
    return result.ok ? succeed(result.roster) : fail(result.error);
  }

  if (!actor) return fail('not_in_room');

  switch (command.type) {
    case 'leave': {
      const withoutMyVote: Room = { ...room, round: { ...room.round, votes: withoutVote(room.round.votes, actorId) } };
      return succeed(applyLeave(withoutMyVote, actorId));
    }

    case 'disconnect':
      return succeed(applyDisconnect(room, actorId));

    case 'setRole': {
      if (actor.role === command.role) return succeed(room);
      const votes = command.role === 'observer' ? withoutVote(room.round.votes, actorId) : room.round.votes;
      const withVotes: Room = { ...room, round: { ...room.round, votes } };
      return succeed(maybeAutoReveal(applySetRole(withVotes, actorId, command.role), now));
    }

    case 'castVote': {
      if (actor.role === 'observer') return fail('observers_cannot_vote');
      if (roundPhase(room.round) === 'revealed') return fail('round_revealed');
      if (!getDeck(room.deckId).cards.includes(command.value)) return fail('card_not_in_deck');

      const votes = { ...room.round.votes, [actorId]: command.value };
      return succeed(maybeAutoReveal({ ...room, round: { ...room.round, votes } }, now));
    }

    case 'retractVote': {
      if (roundPhase(room.round) === 'revealed') return fail('round_revealed');
      return succeed({ ...room, round: { ...room.round, votes: withoutVote(room.round.votes, actorId) } });
    }

    case 'reveal': {
      if (room.hostId !== actorId) return fail('host_only');
      if (roundPhase(room.round) === 'revealed') return fail('round_revealed');
      if (Object.keys(room.round.votes).length === 0) return fail('no_votes');
      return succeed({ ...room, round: { ...room.round, revealedAt: now } });
    }

    case 'reset': {
      if (room.hostId !== actorId) return fail('host_only');
      return succeed({ ...room, round: emptyRound(room.round.title, now) });
    }

    case 'setTitle': {
      if (room.hostId !== actorId) return fail('host_only');
      const title = command.title.trim();
      if (title.length > MAX_TITLE_LENGTH) return fail('invalid_title');
      return succeed({ ...room, round: { ...room.round, title } });
    }

    case 'nextRound': {
      if (room.hostId !== actorId) return fail('host_only');
      const title = command.title.trim();
      if (title.length > MAX_TITLE_LENGTH) return fail('invalid_title');
      const archived = archiveCurrentRound(room, now);

      // An explicit title interjects a story that is not on the list; an empty
      // one means "carry on down the queue".
      if (title.length > 0) return succeed({ ...archived, round: emptyRound(title, now) });

      const [next, ...rest] = archived.queue;
      if (!next) return succeed({ ...archived, round: emptyRound('', now) });
      return succeed({ ...archived, queue: rest, round: emptyRound(next.title, now) });
    }

    case 'setDeck': {
      if (room.hostId !== actorId) return fail('host_only');
      if (!isDeckId(command.deckId)) return fail('unknown_deck');
      if (command.deckId === room.deckId) return succeed(room);
      // Cards from the old deck are meaningless in the new one.
      return succeed({ ...room, deckId: command.deckId, round: emptyRound(room.round.title, now) });
    }

    case 'queueStories': {
      if (room.hostId !== actorId) return fail('host_only');

      const titles: string[] = [];
      for (const raw of command.titles) {
        const title = raw.trim();
        if (title.length === 0) continue;
        if (title.length > MAX_TITLE_LENGTH) return fail('invalid_title');
        titles.push(title);
      }
      if (titles.length === 0) return succeed(room);

      const [first, ...rest] = titles;
      const primed = awaitingFirstStory(room);
      const pending = primed ? rest : titles;
      if (room.queue.length + pending.length > MAX_QUEUE_LENGTH) return fail('queue_full');

      const queued = pending.map(
        (title, index): QueuedStory => ({ id: `${room.id}-s${room.queueSeq + index}`, title }),
      );

      return succeed({
        ...room,
        round: primed ? { ...room.round, title: first as string } : room.round,
        queue: [...room.queue, ...queued],
        queueSeq: room.queueSeq + pending.length,
      });
    }

    case 'transferHost': {
      if (room.hostId !== actorId) return fail('host_only');
      if (command.participantId === actorId) return succeed(room);

      const target = room.participants[command.participantId];
      if (!target) return fail('unknown_participant');
      // Handing the room to someone who has gone away would strand it: nobody
      // could reveal, and re-election only fires when a host disconnects.
      if (!target.connected) return fail('participant_away');

      return succeed({ ...room, hostId: target.id });
    }

    case 'removeParticipant': {
      if (room.hostId !== actorId) return fail('host_only');
      if (command.participantId === actorId) return fail('cannot_remove_self');

      const target = room.participants[command.participantId];
      if (!target) return fail('unknown_participant');
      // Clearing a stale seat, not moderation. A connected person holds a live
      // socket and a valid seat token, so removing them would only have them
      // reconnect and reclaim the seat.
      if (target.connected) return fail('participant_connected');

      const participants = { ...room.participants };
      delete participants[target.id];

      // The seat was what held the round open, so clearing it can complete the
      // table — and the host doing it is as deliberate an act as a vote.
      return succeed(
        maybeAutoReveal(
          { ...room, participants, round: { ...room.round, votes: withoutVote(room.round.votes, target.id) } },
          now,
        ),
      );
    }

    case 'setAutoReveal': {
      if (room.hostId !== actorId) return fail('host_only');
      if (room.autoReveal === command.enabled) return succeed(room);
      // Switching it on with a full table means it is already due.
      return succeed(maybeAutoReveal({ ...room, autoReveal: command.enabled }, now));
    }

    case 'removeQueuedStory': {
      if (room.hostId !== actorId) return fail('host_only');
      const queue = room.queue.filter((story) => story.id !== command.storyId);
      // A double-click on the same row is not an error.
      if (queue.length === room.queue.length) return succeed(room);
      return succeed({ ...room, queue });
    }

    default: {
      const exhaustive: never = command;
      throw new Error(`unhandled command: ${JSON.stringify(exhaustive)}`);
    }
  }
}
